import {Combo} from '@mintdata-wsp/common/components/combo/Combo';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {MemberRole, OrganizationMember} from '@mintdata-wsp/common/state/UIState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Input, InputSize, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {AddPeople} from '@mintdata-wsp/wsp/ui/views/workspace/settings/organization/members/AddPeople';
import {MemberCard} from '@mintdata-wsp/wsp/ui/views/workspace/settings/organization/members/MemberCard';
import {SettingsHeader} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsHeader';
import {SettingsRoutes} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsPanel';
import {SettingsScroll} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsScroll';
import {SettingsSpinner} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsSpinner';
import {IListItemState, ListModel} from '@mintdata/mint-kit/api/models/List';
import {PopupAnchor, PopupSize, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {RouteComponentProps} from 'react-router';

const styles = require('./OrgMembersView.less');
const cx = classnames.bind(styles);

enum ShowFilter
{
    Everyone = 1,
    Admins = 2,
    Members = 3,
    Pending = 4,
}

const showFilterTitles =
    {
        [ShowFilter.Everyone]: 'Everyone',
        [ShowFilter.Admins]: 'Organization Admins',
        [ShowFilter.Members]: 'Organization Members',
        [ShowFilter.Pending]: 'Pending invitations',
    };

@observer
export class OrgMembersView extends React.Component<RouteComponentProps<any>, any>
{
    @observable
    private _search:string = '';

    @observable
    private _showFilter:ShowFilter = ShowFilter.Everyone;

    // TODO for animation
    // @observable
    // private _deletingMemberIds:number[] = [];

    @observable
    private _errors:any[] = [];

    private _showFilterItems;

    @observable
    private _membersLoaded:boolean = false;

    @computed
    private get _visibleMembers()
    {
        const {list} = rootState.ui.members;

        if (!list) return null;

        let visibleMembers = [];

        list.forEach((member:OrganizationMember) =>
        {
            if( this._search )
            {
                if( member.fullName.toLowerCase().indexOf(this._search.toLowerCase()) === -1 )
                    return;
            }
            if( this._showFilter !== ShowFilter.Everyone )
            {
                if( this._showFilter === ShowFilter.Admins && member.role !== MemberRole.Admins )
                    return;
                if( this._showFilter === ShowFilter.Members && member.role !== MemberRole.Members )
                    return;
                if( this._showFilter === ShowFilter.Pending && member.role !== MemberRole.Pending )
                    return;
            }
            visibleMembers.push(member);
        });

        // SORT: Pending -> Admin -> Member
        visibleMembers.sort((a, b) =>
        {
            if( a.role === MemberRole.Admins && b.role === MemberRole.Members )
                return -1;
            if( a.role === MemberRole.Members && b.role === MemberRole.Admins )
                return 1;
            if( a.role === MemberRole.Pending && (b.role === MemberRole.Admins || b.role === MemberRole.Members) )
                return -1;
            if( (a.role === MemberRole.Admins || a.role === MemberRole.Members) && b.role === MemberRole.Pending )
                return 1;
            return 0;
        });

        return visibleMembers;
    }

    componentWillMount()
    {
        controllers.ui.members.syncOrganizationUsers();
        controllers.ui.members.syncOrganizationPendingUsers();

        const showFilterItems = [
            {filterId: ShowFilter.Everyone, title: showFilterTitles[ShowFilter.Everyone]},
            {filterId: ShowFilter.Pending,  title: showFilterTitles[ShowFilter.Pending]},
            {filterId: ShowFilter.Admins,   title: showFilterTitles[ShowFilter.Admins]},
            {filterId: ShowFilter.Members,  title: showFilterTitles[ShowFilter.Members]},
        ];
        this._showFilterItems = new ListModel(showFilterItems);
        this._showFilterItems.selectFirstItem();
    }

    componentDidMount():void
    {
        controllers.analytics.main.trackEvent(AnalyticsEventActions.WSP_PAGE_LOADED);
    }

    @action.bound
    private _searchByName(newValue)
    {
        this._search = newValue;
    }

    @action.bound
    private _onSelectFilter(filterItem)
    {
        this._showFilter = filterItem.filterId;
    }

    @bind
    _filterCaptionRenderer(data:any, state:any, styles:IStyles):JSX.Element
    {
        const {title} = data;
        const {isOpened} = state;

        return (
            <Row className={cx('showFilter-caption', {isOpened})}>
                {/*<span className={cx('showFilter-show')}>Show</span>*/}
                <span className={cx('showFilter-title')}>{title}</span>

                <MDIcon className={cx('showFilter-icon')}
                      name={MDIcons.common.ArrowDropdown}
                      size={MDIconSize.medium16}
                />
            </Row>
        );
    }

    @bind
    _filterItemsRenderer(data:any, state:IListItemState, styles:IStyles):JSX.Element
    {
        const {title} = data;
        const {isSelected} = state;

        return (
            <Row className={cx('showFilter-item', {isSelected})}>
                <MDIcon className={cx('showFilter-icon')}
                      name={MDIcons.common.Checkmark}
                />
                <div>
                    {title}
                </div>
            </Row>
        );
    }

    @action.bound
    private _onRemoveMember(member:OrganizationMember):void
    {
        controllers.ui.members.removeMember(member)
        .then(action((response) =>
        {
            // TODO view errors
            // TODO  "message" : "Cannot remove user from the group: user (id: XX) is only in one group"
            if (ApiError.isError(response))
            {
                if ((response as ApiError).message)
                    this._errors.push({id: member.id, message: (response as ApiError).message});

                return;
            }

            // TODO animation
            // this._deletingMemberIds.push(member.id);
            //
            // setTimeout(action(() =>
            // {
            //     const deletingMemberIndex = this._deletingMemberIds.indexOf(member.id);
            //     this._deletingMemberIds.splice(deletingMemberIndex, 1);
            // }
            // ), 300);
        }))
        .catch(err => { console.warn(err) });
    }

    @action.bound
    private _onCloseError(member:OrganizationMember):void
    {
        this._errors = this._errors.filter(item => item.id != member.id);
    }

    @bind
    private _getErrorMessage(id:number):string | null
    {
        let item = this._errors.filter(item => item.id == id);
        return item.length ? item[0].message : null;
    }

    private onInvitationsSended():void
    {
        controllers.ui.members.syncOrganizationPendingUsers();
    }

    render()
    {
        const members = this._visibleMembers;

        return (
            <Column className={cx('orgMembersSnapIn')}>

                <SettingsHeader
                    text={'Members'}
                    selectedRoute={SettingsRoutes.Members}
                >

                    <AddPeople onInvitationsSended={this.onInvitationsSended}/>

                </SettingsHeader>

                <Row className={styles.searchLine}>

                    <Input
                        placeholder={'Search by name...'}
                        icon={MDIcons.common.Search}
                        style={InputStyle.Simple}
                        size={InputSize.Medium}
                        className={cx('searchByName')}
                        onChange={this._searchByName}
                    />

                    <Combo dataSource={this._showFilterItems}
                           onSelectItem={this._onSelectFilter}
                           contentRenderer={this._filterCaptionRenderer}
                           itemRenderer={this._filterItemsRenderer}
                           sourceAnchor={SourceAnchor.RightBottom}
                           popoverAnchor={PopupAnchor.RightTop}
                           popupSize={PopupSize.Original}
                           theme={{content: cx('showFilter-items')}}
                    >
                    </Combo>

                </Row>

                <SettingsScroll>

                    {members && members.map((member) => { return (
                        <MemberCard
                            key={`${member.groupId}:${member.id}`}
                            member={member}
                            highlight={this._search}
                            onRemove={this._onRemoveMember}
                            errorMessage={this._getErrorMessage(member.id)}
                            onCloseError={this._onCloseError}
                            // className={cx({isDeletingMember: this._deletingMemberIds.indexOf(member.id) !== -1})}
                        />
                    )})}

                    <SettingsSpinner isVisible={members === null}/>

                </SettingsScroll>

            </Column>
        );
    }
}





